<script lang="ts">
  import { onMount } from "svelte";
  import CallItem from "../components/CallItem.svelte";
  import { api, type CallListItem, type CallsStats } from "../lib/api";

  type Filter = "all" | "target" | "spam" | "bad" | "missed" | "not_called_back";

  let {
    filter: initialFilter = "all",
    onback,
    onhome,
  }: { filter?: string; onback?: () => void; onhome?: () => void } = $props();

  const FILTERS: [Filter, string][] = [
    ["all", "Все"],
    ["target", "Целевые"],
    ["not_called_back", "Не перезвонили"],
    ["missed", "Пропущенные"],
    ["bad", "Плохие"],
    ["spam", "Спам и реклама"],
  ];

  let filter = $state<Filter>(
    (["all", "target", "spam", "bad", "missed", "not_called_back"] as string[]).includes(initialFilter) ? (initialFilter as Filter) : "all",
  );
  let days = $state(7);
  let items = $state<CallListItem[]>([]);
  let stats = $state<CallsStats | null>(null);
  let loading = $state(true);
  let error = $state("");

  let seq = 0;

  async function load() {
    const my = ++seq;  // a slower earlier request must not overwrite the current filter
    loading = true;
    error = "";
    try {
      const [l, s] = await Promise.all([api.callsList(filter, days), api.callsStats(days)]);
      if (my !== seq) return;
      items = l.items;
      stats = s;
    } catch (e) {
      if (my === seq) error = e instanceof Error ? e.message : "ошибка";
    } finally {
      if (my === seq) loading = false;
    }
  }

  function setFilter(f: Filter) {
    filter = f;
    const q = new URLSearchParams(location.search);
    q.set("filter", f);
    history.replaceState(null, "", `?${q}`);
    load();
  }

  function setDays(d: number) {
    days = d;
    load();
  }

  onMount(load);
</script>

<div class="screen">
  <header class="hdr">
    <button class="back" onclick={() => onback?.()} aria-label="Назад">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
        stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
    </button>
    <div class="title">Звонки</div>
    <button class="home" onclick={() => onhome?.()} aria-label="Главная">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"
        stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"/></svg>
    </button>
  </header>

  <div class="bar">
    <div class="chips">
      {#each [[7, "7 дней"], [30, "30 дней"]] as [d, label]}
        <button class="chip small" class:active={days === d} onclick={() => setDays(d as number)} type="button">{label}</button>
      {/each}
    </div>
  </div>

  {#if stats}
    <section class="stats">
      <div class="nums">
        <div class="num"><b>{stats.incoming}</b><span>входящих</span></div>
        <div class="num"><b>{stats.answered_pct ?? "—"}{stats.answered_pct != null ? "%" : ""}</b><span>ответили</span></div>
        <div class="num" class:warn={stats.not_called_back > 0}><b>{stats.not_called_back}</b><span>не перезвонили</span></div>
        <div class="num"><b>{stats.callback_median_min ?? "—"}</b><span>мин до перезвона</span></div>
      </div>
      {#if stats.analyzed}
        <div class="split">Разговоров с клиентами <b>{stats.target}</b> · спам, реклама и прочее <b>{stats.non_target}</b></div>
      {/if}
      {#if stats.by_staff.length}
        <div class="staff">
          {#each stats.by_staff.slice(0, 6) as s}
            <div class="srow">
              <span class="sname">{s.name}</span>
              <span class="sval">{s.calls} зв.{#if s.avg_score != null} · {s.avg_score}/10{/if}{#if s.bad} · <em>плохих {s.bad}</em>{/if}</span>
            </div>
          {/each}
        </div>
      {/if}
      {#if stats.objections.length}
        <div class="obj">Клиенты говорят: {stats.objections.map((o) => `${o.text} (${o.count})`).join(", ")}</div>
      {/if}
    </section>
  {/if}

  <div class="bar">
    <div class="chips">
      {#each FILTERS as [f, label]}
        <button class="chip" class:active={filter === f} onclick={() => setFilter(f)} type="button">{label}</button>
      {/each}
    </div>
  </div>

  {#if loading}
    <div class="empty">Загружаю…</div>
  {:else if error}
    <div class="empty err">{error}</div>
  {:else if items.length === 0}
    <div class="empty">
      {filter === "not_called_back" ? "Все пропущенные перезвонили" : filter === "bad" ? "Плохих звонков нет" : filter === "spam" ? "Спама и рекламы нет" : "Звонков нет"}
    </div>
  {:else}
    <section class="list">
      {#each items as c (c.id)}
        <CallItem {c} showLead />
      {/each}
    </section>
  {/if}
</div>

<style>
  .screen { padding-bottom: 96px; }
  .hdr {
    padding: calc(12px + var(--safe-top, 0px)) 16px 12px;
    display: flex; align-items: center; gap: 8px;
    border-bottom: 1px solid var(--border);
  }
  .back, .home {
    width: 36px; height: 36px; border-radius: 10px;
    display: flex; align-items: center; justify-content: center;
    color: var(--text-mute); background: none; border: none;
  }
  .back:active, .home:active { background: var(--bg-elev); }
  .back svg, .home svg { width: 20px; height: 20px; }
  .title { font-family: 'Manrope', sans-serif; font-size: 15px; font-weight: 700; flex: 1; text-align: center; }
  .bar { padding: 12px 16px 4px; }
  .chips { display: flex; gap: 8px; flex-wrap: wrap; }
  .chip {
    background: var(--bg);
    border: 1.5px solid var(--border-strong, var(--border));
    border-radius: 22px;
    padding: 8px 14px;
    color: var(--text);
    font-size: 14px; font-weight: 600;
    font-family: 'Manrope', sans-serif;
    cursor: pointer;
    transition: transform 120ms, background 120ms;
  }
  .chip.small { padding: 6px 12px; font-size: 13px; }
  .chip:active { transform: scale(0.96); }
  .chip.active { background: var(--ember); color: #fff; border-color: var(--ember); font-weight: 700; }
  .stats {
    margin: 8px 16px 4px;
    background: var(--bg-elev);
    border: 1px solid var(--border);
    border-radius: 14px;
    padding: 14px;
  }
  .nums { display: grid; grid-template-columns: repeat(4, 1fr); gap: 6px; }
  .num { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
  .num b { font-family: 'Manrope', sans-serif; font-size: 20px; font-weight: 800; color: var(--text); font-variant-numeric: tabular-nums; }
  .num span { font-size: 11px; line-height: 1.2; color: var(--text-mute); }
  .num.warn b { color: var(--danger); }
  .split { margin-top: 12px; font-size: 13px; color: var(--text-mute); }
  .split b { color: var(--text); }
  .staff { margin-top: 12px; border-top: 1px solid var(--border); padding-top: 10px; display: flex; flex-direction: column; gap: 6px; }
  .srow { display: flex; justify-content: space-between; gap: 8px; font-size: 13px; }
  .sname { color: var(--text); min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .sval { color: var(--text-mute); white-space: nowrap; }
  .sval em { color: var(--danger); font-style: normal; }
  .obj { margin-top: 10px; font-size: 13px; line-height: 1.4; color: var(--text-mute); }
  .list { padding: 8px 16px; display: flex; flex-direction: column; gap: 8px; }
  .empty { padding: 32px 16px; text-align: center; color: var(--text-mute); font-size: 14px; }
  .empty.err { color: var(--danger); }
</style>
